// @catalog F.006
// SyncStatusBadge — dot verde + texto "Sync <fuente> · hace N min".
// Usado en el topbar de la app shell.
import { Box } from "@mui/material";

interface SyncStatusBadgeProps {
  label?: string;
  // Timestamp ISO del último sync. Si no se provee, muestra solo "Listo".
  lastSyncAt?: string | null;
  // Estado del último sync (default "ok").
  status?: "ok" | "warn" | "error";
}

function formatRelative(iso: string): string {
  const now = Date.now();
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diffMs = Math.max(0, now - then);
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return "ahora";
  if (minutes < 60) return `hace ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `hace ${hours} h`;
  const days = Math.floor(hours / 24);
  return `hace ${days} d`;
}

function dotColor(status: "ok" | "warn" | "error"): {
  bg: string;
  ring: string;
} {
  if (status === "ok") return { bg: "var(--pos)", ring: "var(--pos-soft)" };
  if (status === "warn") return { bg: "var(--warn)", ring: "var(--warn-soft)" };
  return { bg: "var(--neg)", ring: "var(--neg-soft)" };
}

export function SyncStatusBadge(props: SyncStatusBadgeProps): JSX.Element {
  const { label = "Sync Odoo", lastSyncAt, status = "ok" } = props;
  const palette = dotColor(status);
  const relativeText = lastSyncAt ? formatRelative(lastSyncAt) : null;

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "8px",
        fontSize: "12px",
        color: "var(--ink-3)",
      }}
    >
      <Box
        component="span"
        aria-hidden="true"
        sx={{
          width: "6px",
          height: "6px",
          borderRadius: "50%",
          backgroundColor: palette.bg,
          boxShadow: `0 0 0 3px ${palette.ring}`,
        }}
      />
      <Box component="span">
        {label}
        {relativeText && (
          <Box component="span" sx={{ ml: "6px" }}>
            · {relativeText}
          </Box>
        )}
      </Box>
    </Box>
  );
}
